import { useState, useEffect } from 'react'
import { stackApi, type AuditEvent } from '../../lib/stack/api'

function XIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

function SpinnerIcon() {
  return (
    <svg className="animate-spin" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  )
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  update: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  delete: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  restore: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  move: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  upload: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300',
  share: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
}

function actionBadge(action: string) {
  const color = ACTION_COLORS[action.toLowerCase()] ?? 'bg-slate-100 dark:bg-slate-700 text-slate-500'
  return <span className={`px-1.5 py-0.5 rounded-full text-2xs font-semibold uppercase ${color}`}>{action}</span>
}

function formatTs(ts: string) {
  return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

interface AuditLogPanelProps {
  onClose: () => void
}

const ACTIONS = ['', 'create', 'update', 'delete', 'restore', 'move', 'upload', 'share']

export default function AuditLogPanel({ onClose }: AuditLogPanelProps) {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterAction, setFilterAction] = useState('')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')

  useEffect(() => {
    setLoading(true)
    setError(null)
    stackApi.getAuditLog({
      action: filterAction || undefined,
      from: filterFrom || undefined,
      to: filterTo || undefined,
    })
      .then(setEvents)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load audit log'))
      .finally(() => setLoading(false))
  }, [filterAction, filterFrom, filterTo])

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-canvas dark:bg-[#131825] animate-in fade-in duration-150">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700/60">
        <div>
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Audit Log</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">All Stack activity for your organisation</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer">
          <XIcon />
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/40">
        <select
          value={filterAction}
          onChange={e => setFilterAction(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer"
        >
          {ACTIONS.map(a => <option key={a} value={a}>{a ? a.charAt(0).toUpperCase() + a.slice(1) : 'All actions'}</option>)}
        </select>
        <input
          type="date"
          value={filterFrom}
          onChange={e => setFilterFrom(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
          placeholder="From"
        />
        <span className="text-slate-400 text-sm">→</span>
        <input
          type="date"
          value={filterTo}
          onChange={e => setFilterTo(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400"
          placeholder="To"
        />
        {(filterAction || filterFrom || filterTo) && (
          <button
            onClick={() => { setFilterAction(''); setFilterFrom(''); setFilterTo('') }}
            className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline cursor-pointer"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">{events.length} events</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32"><SpinnerIcon /></div>
        ) : error ? (
          <div className="px-6 py-8 text-sm text-red-500 dark:text-red-400">{error}</div>
        ) : events.length === 0 ? (
          <div className="px-6 py-12 text-sm text-slate-400 dark:text-slate-500 text-center">No events found</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80">
              <tr className="border-b border-slate-200 dark:border-slate-700/60">
                <th className="px-6 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 w-40">Time</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 w-24">Action</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 w-20">Type</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Node</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Actor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/40">
              {events.map(ev => (
                <tr key={ev.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <td className="px-6 py-3 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatTs(ev.timestamp)}</td>
                  <td className="px-4 py-3">{actionBadge(ev.action)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 capitalize">{ev.nodeType}</td>
                  <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-200 font-medium max-w-xs truncate">{ev.nodeName}</td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-400 dark:text-slate-500 truncate max-w-xs">{ev.actorOid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
